import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ChevronsUpDownIcon, LogOutIcon, Trash2Icon, UserRoundIcon } from "lucide-react";
import { type ReactNode, useState } from "react";

import { useApiErrorMessage } from "@/shared/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { SidebarMenuButton } from "@/shared/ui/sidebar";

import { useCurrentUser } from "../api/useCurrentUser";
import { useLogout } from "../api/useLogout";
import { DeleteAccountDialog } from "./DeleteAccountDialog";

type UserMenuProps = {
  // Подпись под ником (ранг игрока) — её считает другая фича, оболочка передаёт готовой.
  details?: ReactNode;
};

// Игрок внизу бокового меню: аватар, ник и подпись. По нажатию — меню «Профиль», «Выйти»
// и «Удалить аккаунт». В свёрнутом меню остаётся аватар с подсказкой. Без вошедшего
// пользователя ничего не показывает.
export const UserMenu = ({ details }: UserMenuProps) => {
  const { t } = useLingui();
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const errorMessage = useApiErrorMessage(logout.error);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!user) {
    return null;
  }
  const name = user.display_name ?? user.steam_id ?? t`Player`;

  return (
    <>
      {/* Не модальное: иначе меню при закрытии возвращает фокус на себя и отнимает его
          у диалога удаления, который открывает. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton size="lg" tooltip={name}>
            {user.avatar_url === null ? (
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-border text-xs font-semibold text-foreground"
              >
                {name.slice(0, 1).toLocaleUpperCase()}
              </span>
            ) : (
              <img
                src={user.avatar_url}
                alt=""
                className="size-9 shrink-0 rounded-full bg-surface-raised"
              />
            )}
            <span className="flex min-w-0 grow flex-col leading-tight">
              <span className="truncate text-sm font-semibold">{name}</span>
              {details !== undefined && (
                <span className="truncate text-xs text-subtle">{details}</span>
              )}
            </span>
            <ChevronsUpDownIcon aria-hidden="true" className="size-4 shrink-0 text-subtle" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start">
          <DropdownMenuItem asChild>
            <Link to="/profile">
              <UserRoundIcon aria-hidden="true" />
              <Trans>Profile</Trans>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={logout.isPending}
            onSelect={() => {
              logout.mutate({});
            }}
          >
            <LogOutIcon aria-hidden="true" />
            <Trans>Sign out</Trans>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => {
              setDeleteOpen(true);
            }}
          >
            <Trash2Icon aria-hidden="true" />
            <Trans>Delete account</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
      {errorMessage !== null && (
        <span role="alert" className="px-2.5 text-sm text-bad">
          {errorMessage}
        </span>
      )}
    </>
  );
};
